import {useState, useCallback, useEffect} from '@wordpress/element';
import {registerPaymentMethod, registerExpressPaymentMethod} from '@woocommerce/blocks-registry';
import {getSetting} from '@woocommerce/settings';
import {dispatch} from '@wordpress/data';
import SimplePayPal from './simple-paypal';
import {useBreakpointWidth, useLoadPayPalScript} from "../../hooks";
import {usePayPalOptions, usePayPalFundingSources, useProcessPayment, useValidateCheckout} from "./hooks";
import {PaymentMethodCard, PaymentMethodDescription, PaymentMethodLabel} from "../../components";
import {useProcessPaymentFailure} from "../../hooks";
import {isCartPage, isCheckoutPage, isAdminOnly, i18n} from "../../utils";

const getData = (key) => {
    const data = getSetting(key);
    return (key, defaultValue = null) => {
        if (!data.hasOwnProperty(key)) {
            data[key] = defaultValue;
        }
        return data[key];
    };
}

const data = getData('ppcp_data');

const isEnabled = context => {
    const sections = [
        ['paypalEnabled', 'paypalSections'],
        ['payLaterEnabled', 'payLaterSections'],
        ['cardEnabled', 'creditCardSections'],
        ['venmoEnabled', 'venmoSections']
    ];
    let result = false;
    for (const section of sections) {
        if (data(section[0]) && data(section[1]).includes(context)) {
            result = true;
        }
    }
    return result;
}
const ExpressPaymentMethod = ({context = 'express_checkout', ...props}) => {
    return <PayPalPaymentMethod
        context={context}
        isExpress={true}
        paymentMethodId='paymentplugins_ppcp_express'
        {...props}/>;
}

const PayPalPaymentMethod = (
    {
        isExpress = false,
        context,
        billing,
        shippingData,
        eventRegistration,
        emitResponse,
        onError,
        onClick,
        onClose,
        onSubmit,
        activePaymentMethod,
        paymentMethodId,
        cartData,
        checkoutStatus
    }) => {
    const title = data('title');
    const queryParams = getSetting('paypalQueryParams');
    const vault = queryParams.vault === 'true';
    const {billingAddress} = billing;
    const {
        onPaymentSetup,
        onCheckoutFail,
        onCheckoutValidation
    } = eventRegistration;
    const {
        responseTypes,
        noticeContexts
    } = emitResponse;
    const {
        extensions
    } = cartData;
    const [buttonsContainer, setButtonsContainer] = useState();
    const {
        createErrorNotice
    } = dispatch('core/notices');

    useBreakpointWidth({width: 375, node: buttonsContainer});

    if (!isExpress) {
        onError = useCallback((error) => {
            createErrorNotice(error?.message ? error.message : error, {
                context: noticeContexts.PAYMENTS
            });
        }, []);
    }

    const setButtonContainerRef = useCallback(el => {
        setButtonsContainer(el);
    }, []);

    const {
        paymentData,
        setPaymentData,
        clearPaymentData
    } = useProcessPayment({
        isExpress,
        onSubmit,
        billingAddress,
        shippingData,
        onPaymentSetup,
        responseTypes,
        activePaymentMethod,
        paymentMethodId
    });

    useProcessPaymentFailure({
        event: onCheckoutFail,
        onError,
        isExpress,
        responseTypes,
        messageContext: noticeContexts.PAYMENTS,
        setPaymentData,
        paymentMethodId,
        activePaymentMethod
    });

    useValidateCheckout({
        title,
        isExpress,
        emitResponse,
        paymentData,
        onCheckoutValidation,
    });

    const paypal = useLoadPayPalScript(queryParams);

    const {
        getOptions,
        enableButtons,
        disableButtons
    } = usePayPalOptions({
        isExpress,
        paypal,
        vault,
        intent: queryParams.intent,
        buttonStyles: data('buttons'),
        billing,
        shippingData,
        eventRegistration,
        setError: onError,
        setPaymentData,
        onClick,
        onClose,
        needsSetupToken: extensions?.wc_ppcp?.needsSetupToken || false,
        buttonsContainer
    });
    const sources = usePayPalFundingSources({
        data,
        paypal,
        context,
        vault
    });

    useEffect(() => {
        const paymentData = data('paymentData');
        if (paymentData && paymentData.order) {
            setPaymentData(paymentData, false);
        }
    }, []);

    useEffect(() => {
        if (paypal && sources && buttonsContainer) {
            sources.forEach(source => {
                const button = paypal.Buttons(getOptions(source));
                if (button.isEligible()) {
                    button.render(buttonsContainer);
                }
            })
        }
    }, [paypal, sources, buttonsContainer]);

    useEffect(() => {
        if (buttonsContainer) {
            if (checkoutStatus?.isCalculating) {
                disableButtons();
                buttonsContainer.classList.add('disabled');
            } else {
                enableButtons();
                buttonsContainer.classList.remove('disabled');
            }
        }
    }, [enableButtons, disableButtons, checkoutStatus?.isCalculating])

    if (!isExpress && paymentData) {
        if (paymentData.paymentToken) {
            return (
                <PaymentMethodCard
                    description={paymentData.paymentToken?.payment_source?.paypal?.email_address}
                    icon={data('icons').find(icon => icon.id === 'paypal_simple')}
                    label={i18n.cancel}
                    onCancel={clearPaymentData}/>
            );
        }
        return (
            <PaymentMethodCard
                description={paymentData.order.payer.email_address}
                icon={data('icons').find(icon => icon.id === 'paypal_simple')}
                label={i18n.cancel}
                onCancel={clearPaymentData}/>
        )
    }

    return (
        <>
            {!isExpress &&
                <PaymentMethodDescription description={data('description')}/>
            }
            <div className='wc-ppcp-paypal__buttons' ref={setButtonContainerRef}></div>
        </>
    );
}

let context = 'express_checkout';
if (isCartPage()) {
    context = 'cart';
}
registerExpressPaymentMethod({
    name: 'paymentplugins_ppcp_express',
    gatewayId: 'ppcp',
    paymentMethodId: 'ppcp',
    canMakePayment: () => {
        return !isAdminOnly() && ((isCartPage() && isEnabled('cart'))
            || (isCheckoutPage() && isEnabled('express_checkout')));
    },
    content: <ExpressPaymentMethod context={context}/>,
    edit: <ExpressPaymentMethod context={context}/>,
    supports: {
        features: data('features')
    }
});

if (data('placeOrderButtonEnabled')) {
    registerPaymentMethod({
        name: 'ppcp',
        label: <PaymentMethodLabel
            id='ppcp'
            title={data('title')}
            icons={data('icons').find(icon => icon.id === 'paypal')}/>,
        ariaLabel: 'PayPal',
        canMakePayment: () => {
            return !isAdminOnly() && isCheckoutPage();
        },
        content: <SimplePayPal data={data}/>,
        edit: <SimplePayPal data={data}/>,
        placeOrderButtonLabel: data('i18n').buttonLabel,
        supports: {
            showSavedCards: false,
            showSaveOption: false,
            features: data('features')
        }
    });
} else {
    registerPaymentMethod({
        name: 'ppcp',
        label: <PaymentMethodLabel
            id='ppcp'
            title={data('title')}
            icons={data('icons').find(icon => icon.id === 'paypal')}/>,
        ariaLabel: 'PayPal',
        canMakePayment: () => {
            return !!isEnabled('checkout') && !isAdminOnly() && isCheckoutPage();
        },
        content: <PayPalPaymentMethod context={'checkout'} paymentMethodId={'ppcp'}/>,
        edit: <PayPalPaymentMethod context={'checkout'} paymentMethodId={'ppcp'}/>,
        supports: {
            showSavedCards: false,
            showSaveOption: false,
            features: data('features')
        }
    });
}



